// ─────────────────────────────────────────────────────────────────────────────

/**
 * Marks every local `<img>` (root-relative `src` or same-origin URL) with
 * `data-ot-ignore` so OneTrust's auto-block scanner skips it (its check is
 * literally `hasAttribute('data-ot-ignore')`). Existing images are marked
 * synchronously; future ones are caught by a `MutationObserver` watching
 * `document.body`. Idempotent — repeat calls are no-ops.
 *
 * Must be called BEFORE OneTrust's scripts are appended so this observer
 * wins the race against OneTrust's own scanner. Third-party images are left
 * alone so consent gating still applies to them.
 */
export function addLocalImageSkip() {
  if (state.hasAdded) return;

  state.hasAdded = true;

  document.querySelectorAll('img').forEach(markIfLocal);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach(processMutation);
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function processMutation(mutation: MutationRecord) {
  mutation.addedNodes.forEach(processAddedNode);
}

function processAddedNode(node: Node) {
  if (node instanceof HTMLImageElement) {
    markIfLocal(node);

    return;
  }

  if (node instanceof Element) {
    node.querySelectorAll('img').forEach(markIfLocal);
  }
}

function markIfLocal(img: HTMLImageElement) {
  if (!checkIfLocalImage(img)) return;

  img.setAttribute('data-ot-ignore', 'true');
}

function checkIfLocalImage(img: HTMLImageElement) {
  const src = img.getAttribute('src');

  if (!src) return false;

  // Root-relative path (e.g. `/assets/foo.png`). Excludes protocol-relative
  // URLs like `//cdn.example.com/foo.png`, which resolve to the page's
  // protocol but a different origin.
  if (src.startsWith('/') && !src.startsWith('//')) return true;

  // Absolute same-origin URL.
  return src.startsWith(window.location.origin);
}

// ─── State ───────────────────────────────────────────────────────────────────

const state = { hasAdded: false };
